/**
 * App.js — Root Application Shell
 *
 * Responsibilities (after refactor + router migration):
 *   1. Layout: sidebar navigation, top bar, page content area.
 *   2. Central data store (entities shared across all pages).
 *   3. RBAC: fetching allowed views and gating route access.
 *   4. URL-based page rendering via React Router <Routes>/<Route>.
 *
 * Extracted concerns:
 *   - Authentication  → src/context/AuthContext.js   (useAuth)
 *   - Theme           → src/context/ThemeContext.js  (useTheme)
 *   - Notifications   → src/context/NotificationContext.js (useNotifications)
 *
 * Navigation:
 *   - Each page has a canonical URL (e.g., /vehicles, /drivers).
 *   - The sidebar uses <Link> for navigation; the URL is the source of truth.
 *   - `activePage` is derived from the current URL path, not stored as state.
 */

import React, { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { fleetApi, setSessionExpiredCallback } from './api/fleetApi';
import { useLanguage } from './i18n/LanguageContext';
import { useAuth } from './context/AuthContext';
import { ViewPermissionsProvider, normalizeViewKey, buildViewPermissionMap, lookupViewPermissions } from './context/ViewPermissionsContext';
import { useTheme } from './context/ThemeContext';
import { useNotifications } from './context/NotificationContext';
import { useTrackAsync } from './hooks/useBlockingTracker';
import { useMyPlanQuery } from './hooks/queries/useSubscriptionQuery';
import { formatBytes, mbToBytes } from './utils/format';
import AuthPage from './pages/AuthPage';
import FirstLoginPage from './pages/FirstLoginPage';
import { NotificationPanel, NotificationToast, AnnouncementModal } from './components/NotificationCenter';
import LoadingOverlay from './components/LoadingOverlay';
import CommandPalette from './components/CommandPalette';
import PlanExpiredModal from './components/PlanExpiredModal';
import { unsavedGridChanges, confirmLeaveGrid } from './components/DataGrid';
import Sidebar from './components/Sidebar';
import { useNavSignals } from './hooks/useNavSignals';
import {
  LayoutDashboard, Truck, Users, ClipboardList,
  Layers, MapPin, Contact2, Building2, CreditCard,
  Menu, Bell, Search, Navigation, Shield,
  Globe, Lock, Settings, Moon, Megaphone, Filter,
  Package, ShoppingCart, RotateCcw, FileText, Wrench, SlidersHorizontal, Boxes, Fuel,
  ShieldCheck, Hexagon, Store, Map as MapIcon,
} from 'lucide-react';
import './App.css';
// Generated from App.css by tools/gen-wide-type.js: type steps up on big monitors.
import './wide-screens.css';

const Dashboard     = lazy(() => import('./pages/Dashboard'));
const Vehicles      = lazy(() => import('./pages/Vehicles'));
const Drivers       = lazy(() => import('./pages/Drivers'));
const Orders        = lazy(() => import('./pages/Orders'));
const TrackingMap   = lazy(() => import('./pages/TrackingMap'));
const Fleets        = lazy(() => import('./pages/Fleets'));
const Places        = lazy(() => import('./pages/Places'));
const Contacts      = lazy(() => import('./pages/Contacts'));
const Vendors       = lazy(() => import('./pages/Vendors'));
const Transactions  = lazy(() => import('./pages/Transactions'));
const Organization  = lazy(() => import('./pages/Organization'));
const Security      = lazy(() => import('./pages/Security'));
const Zones         = lazy(() => import('./pages/Zones'));
const Geofences     = lazy(() => import('./pages/Geofences'));
const Customers     = lazy(() => import('./pages/Customers'));
const SettingsPage  = lazy(() => import('./pages/Settings'));
const Items         = lazy(() => import('./pages/Items'));
const SellOrders       = lazy(() => import('./pages/SellOrders'));
const VehiclesTracker          = lazy(() => import('./pages/VehiclesTracker'));
const RequiredDocumentsSetup   = lazy(() => import('./pages/RequiredDocumentsSetup'));
const DocumentTypes            = lazy(() => import('./pages/DocumentTypes'));
const DefaultSettings          = lazy(() => import('./pages/DefaultSettings'));
const VehicleInventory         = lazy(() => import('./pages/VehicleInventory'));
const MaintenanceRepair        = lazy(() => import('./pages/MaintenanceRepair'));
const FuelPage                 = lazy(() => import('./pages/Fuel'));
const PlanAdmin                = lazy(() => import('./pages/PlanAdmin'));
const SysAdmin                 = lazy(() => import('./pages/SysAdmin'));

// ── Navigation config ──────────────────────────────────────────────────────────

// Recursively flattens the tree returned by getViewsByUserId (children-based) into a flat array.
// Used to give ProtectedRoute and RBAC redirect logic the full list of accessible views.
// Minimum gap between background refreshes of the views tree on tab focus.
const ACCESS_REFRESH_MIN_MS = 60 * 1000;

const flattenViewTree = (nodes) => {
  const result = [];
  for (const node of (nodes || [])) {
    result.push(node);
    if (node.children && node.children.length > 0) {
      result.push(...flattenViewTree(node.children));
    }
  }
  return result;
};

const VIEW_ICON_MAP = {
  dashboard: LayoutDashboard,
  orders: ClipboardList,
  tracking: Navigation,
  'tracking map': Navigation,
  vehicles: Truck,
  'vehicles tracker':  Navigation,
  'vehiclestracker':   Navigation,
  'vehicles-tracker':  Navigation,
  /* One icon per page (Oct 2026). Customers and Contacts shared a card icon
     and Drivers shared one with Users; in the collapsed rail the icon is the
     only clue, so each page needs its own: Drivers the licence card,
     Customers a shop, Contacts people, Zones a map. */
  drivers: Contact2,
  fleets: Layers,
  places: MapPin,
  contacts: Users,
  customers: Store,
  vendors: Building2,
  transactions: CreditCard,
  items:        Package,
  sellorders:    ShoppingCart,
  'sell-orders': ShoppingCart,
  'sell orders': ShoppingCart,
  organization: Globe,
  organizations: Globe,
  zones: MapIcon,
  geofences: Hexagon,
  security:                 Lock,
  'theme-settings':         Settings,
  settings:                 Settings,
  'app settings':           Settings,
  'security/users':         Users,
  'security/roles':         Shield,
  'security/views':         Globe,
  'settings/theme':         Moon,
  'user-settings':          Moon,
  'user settings':          Moon,
  'usersettings':           Moon,
  'settings/user-settings': Moon,
  'default-settings':                 SlidersHorizontal,
  'defaultsettings':                  SlidersHorizontal,
  'default settings':                 SlidersHorizontal,
  'settings/default-settings':        SlidersHorizontal,
  'plans':                            CreditCard,
  'plan-admin':                       CreditCard,
  'settings/plans':                   CreditCard,
  'plans & subscriptions':            CreditCard,
  'sysadmin':                         ShieldCheck,
  'sys-admin':                        ShieldCheck,
  'system administration':            ShieldCheck,
  'sysadmin/organizations':           ShieldCheck,
  'settings/notifications': Bell,
  'settings/financial':     CreditCard,
  'settings/orderconfig':       ClipboardList,
  'required-documents':          FileText,
  'requireddocuments':           FileText,
  'required documents setup':    FileText,
  'settings/required-documents': FileText,
  '/settings/required-documents': FileText,
  'document-types':              FileText,
  'documenttypes':               FileText,
  'document types':              FileText,
  'settings/document-types':     FileText,
  'vehicles-parent':             Truck,
  'vehicle-inventory':           Boxes,
  'maintenance-repair':          Wrench,
  'maintenancerepair':           Wrench,
  'maintenance & repair':        Wrench,
  'maintenance and repair':      Wrench,
  'fuel':                        Fuel,
  'fuel-management':             Fuel,
  '/fuel-management':            Fuel,
  'fuelmanagement':              Fuel,
  'fuel management':             Fuel,
  'fuel-reports':                Fuel,
  '/fuel-reports':               Fuel,
  'fuelreports':                 Fuel,
  'fuel reports':                Fuel,
  'fuel/log':                    Fuel,
  'fuel/consumption':            Fuel,
  'fuel/estimate':               Fuel,
};

// Maps view ID (from backend) → canonical URL path.
// Multiple aliases (e.g., 'tracking map', 'trackingmap') collapse to one clean URL.
const VIEW_PATH_MAP = {
  'dashboard':       '/dashboard',
  'orders':          '/orders',
  'tracking':        '/tracking',
  'tracking map':    '/tracking',
  'trackingmap':     '/tracking',
  'vehicles':          '/vehicles',
  'vehicles tracker':  '/vehicles-tracker',
  'vehiclestracker':   '/vehicles-tracker',
  'vehicles-tracker':  '/vehicles-tracker',
  'drivers':         '/drivers',
  'fleets':          '/fleets',
  'places':          '/places',
  'contacts':        '/contacts',
  'customers':       '/customers',
  'vendors':         '/vendors',
  'transactions':    '/transactions',
  'items':           '/items',
  'sell-orders':     '/sell-orders',
  'sellorders':      '/sell-orders',
  'sell orders':     '/sell-orders',
  'admin-users':     '/security/users',
  'adminusers':      '/security/users',
  'user management': '/security/users',
  'user-management': '/security/users',
  'organization':    '/organization',
  'organizations':   '/organization',
  'zones':           '/zones',
  'geofences':       '/geofences',
  'security':               '/security',
  'theme-settings':         '/settings',
  'settings':               '/settings',
  'app settings':           '/settings',
  'dashboard/general':       '/dashboard/general',
  'dashboard/compliance':    '/dashboard/compliance',
  'dashboard/transactions':  '/dashboard/transactions',
  'security/users':         '/security/users',
  'security/roles':         '/security/roles',
  'security/views':         '/security/views',
  'settings/theme':         '/settings/theme',
  'user-settings':          '/settings/theme',
  'user settings':          '/settings/theme',
  'usersettings':           '/settings/theme',
  'settings/user-settings': '/settings/theme',
  'default-settings':                 '/settings/default-settings',
  'defaultsettings':                  '/settings/default-settings',
  'default settings':                 '/settings/default-settings',
  'settings/default-settings':        '/settings/default-settings',
  'plans':                            '/settings/plans',
  'plan-admin':                       '/settings/plans',
  'settings/plans':                   '/settings/plans',
  'plans & subscriptions':            '/settings/plans',
  'sysadmin':                         '/sysadmin/organizations',
  'sys-admin':                        '/sysadmin/organizations',
  'system administration':            '/sysadmin/organizations',
  'sysadmin/organizations':           '/sysadmin/organizations',
  'settings/notifications': '/settings/notifications',
  'settings/financial':     '/settings/financial',
  'settings/orderconfig':       '/settings/orderconfig',
  'required-documents':          '/settings/required-documents',
  'requireddocuments':           '/settings/required-documents',
  'required documents setup':    '/settings/required-documents',
  'settings/required-documents':  '/settings/required-documents',
  '/settings/required-documents': '/settings/required-documents',
  'document-types':               '/settings/document-types',
  'documenttypes':                '/settings/document-types',
  'document types':               '/settings/document-types',
  'settings/document-types':      '/settings/document-types',
  'vehicle-inventory':            '/vehicle-inventory',
  'maintenance-repair':           '/maintenance-repair',
  '/maintenance-repair':          '/maintenance-repair',
  'maintenancerepair':            '/maintenance-repair',
  'maintenance & repair':         '/maintenance-repair',
  'maintenance and repair':       '/maintenance-repair',
  'fuel':                         '/fuel/log',
  'fuel/log':                     '/fuel/log',
  'fuel/consumption':             '/fuel/consumption',
  'fuel/estimate':                '/fuel/estimate',
  '/fuel':                        '/fuel/log',
  'fuel-management':              '/fuel/log',
  '/fuel-management':             '/fuel/log',
  'fuelmanagement':               '/fuel/log',
  'fuel management':              '/fuel/log',
  'fuel-log':                     '/fuel/log',
  'fuel-reports':                 '/fuel/consumption',
  '/fuel-reports':                '/fuel/consumption',
  'fuelreports':                  '/fuel/consumption',
  'fuel-report':                  '/fuel/consumption',
  'fuel reports':                 '/fuel/consumption',
  'fuel report':                  '/fuel/consumption',
  'fuel usage report':            '/fuel/consumption',
  'fuel-consumption':             '/fuel/consumption',
  'fuel-estimate':                '/fuel/estimate',
};

/**
 * Coerce a nav target into a safe in-app path.
 *
 * `mapTreeNodeToItem` used to fall back to `/${key}` built straight from the
 * backend View's free-text `url`. If that text carried a protocol or a
 * leading `//`, React Router's <Link to> treats it as an absolute destination
 * and navigates the browser clean off the app — which is exactly how a Fuel
 * view produced `http://fuel-reports/` in the address bar.
 *
 * Anything unrecognised is forced to a single-slash relative path so a
 * mislabelled View can, at worst, land on an in-app 404 instead of leaving
 * the site entirely.
 */
/**
 * normalizeViewKey (imported from ViewPermissionsContext) turns a backend
 * View's `url`/`name` into a VIEW_PATH_MAP lookup key.
 *
 * The stored value is free text and inconsistent: some records carry a leading
 * slash ("/fuel-reports"), others don't ("orders"). Every lookup site used to
 * do a bare `.toLowerCase()`, so slashed records silently missed the map and
 * fell through to a raw path — which is how "Fuel Usage Report" ended up
 * pointing at /fuel-reports with no route behind it. It is shared with the
 * per-view permission lookup so both resolve a view the same way.
 */

const toInternalPath = (key) => {
  if (!key) return '/dashboard';
  const mapped = VIEW_PATH_MAP[key];
  if (mapped) return mapped;
  const cleaned = String(key)
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:/i, '') // strip any scheme (http:, javascript:, …)
    .replace(/^\/+/, '')                 // strip leading slashes, incl. protocol-relative //
    .replace(/\s+/g, '-');
  return `/${cleaned}`;
};

// Reverse map: URL path → canonical view ID (used to derive activePage from URL).
const PATH_TO_ID = {
  '/dashboard':             'dashboard',
  '/orders':                'orders',
  '/tracking':              'tracking',
  '/vehicles':              'vehicles',
  '/vehicles-tracker':      'vehicles-tracker',
  '/drivers':               'drivers',
  '/fleets':                'fleets',
  '/places':                'places',
  '/contacts':              'contacts',
  '/customers':             'customers',
  '/vendors':               'vendors',
  '/transactions':          'transactions',
  '/items':                 'items',
  '/sell-orders':           'sell-orders',
  '/organization':          'organization',
  '/zones':                 'zones',
  '/geofences':             'geofences',
  '/security':              'security',
  '/settings':              'settings',
  '/settings/theme':        'settings',
  '/settings/default-settings': 'default-settings',
  // All three tabs resolve to one id so the permission covers the whole page.
  // Omitting a tab here does not just break the sidebar highlight — the RBAC
  // check below resolves currentViewId through this map and bounces the user
  // off any path it cannot resolve.
  '/settings/plans':               'plan-admin',
  '/settings/plans/subscriptions': 'plan-admin',
  '/settings/plans/signup-codes':  'plan-admin',
  '/sysadmin':               'sysadmin',
  '/sysadmin/organizations': 'sysadmin',
  '/sysadmin/users':         'sysadmin',
  '/settings/notifications': 'settings',
  '/settings/financial':    'settings',
  '/settings/orderconfig':  'settings',
  '/dashboard/general':       'dashboard',
  '/dashboard/compliance':    'dashboard',
  '/dashboard/transactions':  'dashboard',
  '/security/users':        'security',
  '/security/roles':        'security',
  '/security/views':            'security',
  '/settings/required-documents':  'required-documents',
  '/settings/document-types':      'document-types',
  '/maintenance-repair':           'maintenance-repair',
  '/vehicle-inventory':            'vehicle-inventory',
  '/fuel':                         'fuel',
  '/fuel/log':                     'fuel',
  '/fuel/consumption':             'fuel',
  '/fuel/estimate':                'fuel',
  '/fuel-management':              'fuel',
  '/fuel-reports':                 'fuel',
};


// Security sub-navigation items shown in sidebar when on any /security/* path.


// Maps view ID → translation key for sidebar item labels
const VIEW_LABEL_KEY = {
  'dashboard':       'navDashboard',
  'orders':          'navOrders',
  'tracking':        'navTracking',
  'tracking map':    'navTracking',
  'trackingmap':     'navTracking',
  'vehicles':        'navVehicles',
  'drivers':         'navDrivers',
  'fleets':          'navFleets',
  'places':          'navPlaces',
  'contacts':        'navContacts',
  'customers':       'navCustomers',
  'vendors':         'navVendors',
  'transactions':    'navTransactions',
  'items':           'navItems',
  'sell-orders':     'navSellOrders',
  'sellorders':      'navSellOrders',
  'sell orders':     'navSellOrders',
  'organization':    'navOrganization',
  'organizations':   'navOrganization',
  'zones':           'navZones',
  'geofences':       'navGeofences',
  'security':        'navSecurity',
  'theme-settings':  'navSettings',
  'settings':        'navSettings',
  'app settings':                 'navSettings',
  'settings/theme':               'navUserSettings',
  'user-settings':                'navUserSettings',
  'user settings':                'navUserSettings',
  'usersettings':                 'navUserSettings',
  'settings/user-settings':       'navUserSettings',
  'default-settings':             'navSystemDefaults',
  'defaultsettings':              'navSystemDefaults',
  'default settings':             'navSystemDefaults',
  'settings/default-settings':    'navSystemDefaults',
  'plans':                        'navPlans',
  'plan-admin':                   'navPlans',
  'settings/plans':               'navPlans',
  'plans & subscriptions':        'navPlans',
  'sysadmin':                     'navSysAdmin',
  'sys-admin':                    'navSysAdmin',
  'system administration':        'navSysAdmin',
  'sysadmin/organizations':       'navSysAdmin',
  'required-documents':           'navRequiredDocuments',
  'requireddocuments':            'navRequiredDocuments',
  'required documents setup':     'navRequiredDocuments',
  'settings/required-documents':  'navRequiredDocuments',
  '/settings/required-documents': 'navRequiredDocuments',
  'document-types':               'navDocumentTypes',
  'documenttypes':                'navDocumentTypes',
  'document types':               'navDocumentTypes',
  'settings/document-types':      'navDocumentTypes',
  'vehicle-inventory':            'navVehicleInventory',
  'fuel':                         'navFuel',
  'fuel/log':                     'navFuel',
  'fuel/consumption':             'navFuel',
  'fuel/estimate':                'navFuel',
  'fuel-management':              'navFuelManagement',
  '/fuel-management':             'navFuelManagement',
  'fuelmanagement':               'navFuelManagement',
  'fuel management':              'navFuelManagement',
  'fuel-reports':                 'navFuelReports',
  '/fuel-reports':                'navFuelReports',
  'fuelreports':                  'navFuelReports',
  'fuel reports':                 'navFuelReports',
};

// ── ProtectedRoute ────────────────────────────────────────────────────────────
// Wraps a route element with an RBAC check against the user's allowedViews list.
// Defined at module level (not inside renderRoutes) to avoid remount on re-render.
function ProtectedRoute({ viewId, allowedViews, element }) {
  // Match on the canonical view ID, not on exact path equality. A page with
  // sub-tabs maps each tab to a different path (/fuel/log vs /fuel/consumption),
  // so a permission granted via one tab's alias would otherwise fail to unlock
  // the others — even though they are the same page and the same permission.
  const targetPath = VIEW_PATH_MAP[viewId];
  const targetId   = PATH_TO_ID[targetPath] || viewId;
  const allowed =
    allowedViews === null ||
    allowedViews.some((v) => {
      const mapped = VIEW_PATH_MAP[normalizeViewKey(v)];
      if (!mapped) return false;
      return mapped === targetPath || PATH_TO_ID[mapped] === targetId;
    });

  if (!allowed) {
    return (
      <div className="section-card" style={{ padding: 40, textAlign: 'center' }}>
        <Shield size={48} style={{ color: 'var(--text-muted)', marginBottom: 16 }} />
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Access Denied</div>
        <div style={{ color: 'var(--text-muted)' }}>You don't have permission to access this page.</div>
      </div>
    );
  }
  return element;
}

export default function App() {
  const { lang, setLang, t } = useLanguage();
  const location  = useLocation();
  const navigate  = useNavigate();

  // ── Contexts ──────────────────────────────────────────────────────────────────
  const { theme, setTheme } = useTheme();
  const trackAsync = useTrackAsync();
  const {
    currentUser, authToken,
    firstLoginSession, setFirstLoginSession,
    login, logout, completeFirstLogin,
    signalSessionExpired,
  } = useAuth();

  // Wire 401 session-expired callback once so expired tokens trigger clean logout
  const expiredWired = useRef(false);
  useEffect(() => {
    if (expiredWired.current) return;
    expiredWired.current = true;
    setSessionExpiredCallback(signalSessionExpired);
  }, [signalSessionExpired]);

  // Derive the active page ID from the current URL path.
  // This replaces the old `activePage` useState.
  const activePage = PATH_TO_ID[location.pathname] || 'dashboard';

  // ── Layout state ──────────────────────────────────────────────────────────────
  /* Open or collapsed is remembered per browser (Oct 2026). Only a press of
     the toggle writes it: the tracking page collapses the sidebar on its own
     to give the map room, and that is not a preference. */
  const [sidebarOpen, setSidebarOpen]     = useState(() => {
    try { return localStorage.getItem('nav_open') !== '0'; } catch (_) { return true; }
  });
  const toggleSidebar = () => {
    setSidebarOpen((o) => {
      try { localStorage.setItem('nav_open', o ? '0' : '1'); } catch (_) { /* storage unavailable */ }
      return !o;
    });
  };
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Groups no longer fold (Oct 2026): all nineteen pages fit on a laptop
  // screen, so folding only ever hid something. The old 'nav_collapsed_cats'
  // key is simply no longer read. A page's own sub-list still opens and
  // closes, and that choice persists.
  const readNavSet = (key) => {
    try { return new Set(JSON.parse(localStorage.getItem(key) || '[]')); }
    catch (_) { return new Set(); }
  };
  const [collapsedSubnavs, setCollapsedSubnavs] = useState(() => readNavSet('nav_collapsed_subnavs'));
  useEffect(() => {
    try { localStorage.setItem('nav_collapsed_subnavs', JSON.stringify([...collapsedSubnavs])); } catch (_) {}
  }, [collapsedSubnavs]);

  const toggleSubnav = (key) => {
    setCollapsedSubnavs(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const [searchQuery, setSearchQuery]   = useState('');
  const [showAnnouncement, setShowAnnouncement] = useState(false);

  // ── ⌘K command palette ───────────────────────────────────────────────────────
  const [showPalette, setShowPalette] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setShowPalette(p => !p);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Tracking map overlay state (lifted so topbar buttons can control TrackingMap) ──
  const isTrackingPage = location.pathname === '/tracking';
  const [trackingVehiclesOpen,     setTrackingVehiclesOpen]     = useState(true);
  const [trackingActiveChip,       setTrackingActiveChip]       = useState(null);
  const [trackingChipStats,        setTrackingChipStats]        = useState({ moving: 0, idle: 0, noSignal: 0 });
  const [trackingChipRects,        setTrackingChipRects]        = useState({});
  const [trackingMapResetVersion,  setTrackingMapResetVersion]  = useState(0);
  const [trackingMapSearch,        setTrackingMapSearch]        = useState('');
  const [trackingMapSearchVersion, setTrackingMapSearchVersion] = useState(0);
  const [trackingMapSuggestions,     setTrackingMapSuggestions]     = useState([]);
  const [trackingMapSuggestionsOpen, setTrackingMapSuggestionsOpen] = useState(false);
  const [trackingMapFlyTo,           setTrackingMapFlyTo]           = useState({ lat: null, lon: null, v: 0 });

  // Auto-close sidebar and auto-open vehicles panel when entering tracking route.
  // Also close the mobile drawer on every route change.
  useEffect(() => {
    setMobileNavOpen(false);
    if (location.pathname === '/tracking') {
      setSidebarOpen(false);
      setTrackingVehiclesOpen(true);
    }
  }, [location.pathname]);

  // Close mobile drawer on Escape key.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setMobileNavOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Debounced autocomplete suggestions for tracking map search
  useEffect(() => {
    if (trackingMapSearch.trim().length < 3) {
      setTrackingMapSuggestions([]);
      setTrackingMapSuggestionsOpen(false);
      return;
    }
    const timer = setTimeout(() => {
      fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(trackingMapSearch.trim())}&format=json&limit=5`)
        .then(r => r.json())
        .then(results => {
          setTrackingMapSuggestions(results);
          setTrackingMapSuggestionsOpen(results.length > 0);
        })
        .catch(() => {});
    }, 350);
    return () => clearTimeout(timer);
  }, [trackingMapSearch]);

  // ── RBAC state ────────────────────────────────────────────────────────────────
  const [allowedViews, setAllowedViews] = useState(null);
  const [navTree,      setNavTree]      = useState([]);
  const lastAccessFetchRef = useRef(0);
  // Ref so the RBAC effect can snapshot the current path without location.pathname being a dep trigger
  const pathnameRef = useRef(location.pathname);
  useEffect(() => { pathnameRef.current = location.pathname; }, [location.pathname]);
  const navigateRef = useRef(navigate);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  // ── Notification hook ─────────────────────────────────────────────────────────
  const {
    unreadCount,
    notifications,        hasMore,      listLoading,
    loadNotifications,    markRead,    markAllRead,   deleteNotification,
    showNotifPanel,       setShowNotifPanel,   openPanel,
    toastQueue,           addToast,    dismissToast,
    notifSettings,        saveAndSetNotifSettings,
    currencySettings,     saveAndSetCurrencySettings,
    orderConfig,          saveAndSetOrderConfig,
  } = useNotifications(currentUser, authToken, fleetApi);

  // ── Organization plan ─────────────────────────────────────────────────────────
  // Shares the ['myPlan'] cache with the Vehicles page and the Settings storage
  // card, so the sidebar costs no extra request. Must sit above the auth gates
  // below — a hook after an early return is skipped on logged-out renders and
  // throws "rendered fewer hooks than expected" the moment someone logs in.
  // Safe to call unconditionally: the query is disabled while authToken is ''.
  const { data: myPlan } = useMyPlanQuery(fleetApi, authToken);

  /* "Needs you" dots in the sidebar (Oct 2026). Read only, from the same
     cached lists the pages use, and only for pages this user can open: until
     the permissions are in, nothing is fetched. Above the auth gates for the
     same reason as useMyPlanQuery. */
  const canSeeView = (id) => Array.isArray(allowedViews) && allowedViews.some((v) => {
    const mapped = VIEW_PATH_MAP[normalizeViewKey(v)];
    return mapped && PATH_TO_ID[mapped] === id;
  });
  // `|| 1` matches the pages' own fallback, so the cache key is the same one.
  const navSignals = useNavSignals(fleetApi, currentUser ? (currentUser.organizationId || 1) : null, authToken, canSeeView);

  // Expired-subscription notice: informational, dismissible, once per browser
  // tab. sessionStorage rather than localStorage because the reminder should
  // return next time they open the app but not on every refresh — and
  // readStoredSession() restores currentUser synchronously on reload, so a
  // "user appeared" effect alone would re-fire on every F5.
  //
  // The ref holds the user id it fired for, not a bare boolean, so a logout →
  // different-user login in the same tab still gets the notice. It is also what
  // actually prevents a reopen when react-query refetches on window focus,
  // since the sessionStorage read sits inside a try that can throw.
  const [showPlanExpired, setShowPlanExpired] = useState(false);
  const planExpiredShownFor = useRef(null);
  useEffect(() => {
    const userId = currentUser?.id;
    if (!userId || !myPlan?.isExpired) return;
    if (planExpiredShownFor.current === userId) return;
    try {
      if (sessionStorage.getItem(`fleet_plan_expired_ack_${userId}`) === '1') {
        planExpiredShownFor.current = userId;
        return;
      }
    } catch (_) { /* storage unavailable — show once this mount */ }
    planExpiredShownFor.current = userId;
    setShowPlanExpired(true);
  }, [currentUser?.id, myPlan]);

  // Signing out clears the latch, so the next sign-in shows the notice again.
  // Without this the flag outlived the session and a logout → login as the same
  // user stayed silent — the reminder is meant to land on every login, and only
  // a refresh should be exempt.
  useEffect(() => {
    if (currentUser) return;
    const lastUserId = planExpiredShownFor.current;
    planExpiredShownFor.current = null;
    setShowPlanExpired(false);
    try {
      if (lastUserId) sessionStorage.removeItem(`fleet_plan_expired_ack_${lastUserId}`);
    } catch (_) { /* storage unavailable — the ref reset already covers this tab */ }
  }, [currentUser]);

  // ── Unsaved table edits (Round 4, Oct 2026) ────────────────────────────────
  // A link to another page while a table holds edits that are not saved asks
  // first. Captured on the document so it runs before the router sees the
  // click; a "no" stops the navigation and leaves the edits where they were.
  useEffect(() => {
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a || a.target === '_blank') return;
      const href = a.getAttribute('href') || '';
      if (!href.startsWith('/') || href === window.location.pathname) return;
      if (unsavedGridChanges() > 0 && !confirmLeaveGrid()) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  // ── Auth coordination ─────────────────────────────────────────────────────────
  // On logout, reset search and navigate home. allowedViews is reset by the RBAC effect below.
  useEffect(() => {
    if (!currentUser) {
      setSearchQuery('');
      navigate('/login', { replace: true });
    }
  }, [currentUser, navigate, setSearchQuery]);

  // ── Fetch user's allowed views (RBAC) ─────────────────────────────────────────
  useEffect(() => {
    if (!currentUser?.id || !authToken) {
      setAllowedViews(null);
      return;
    }

    // `isRefresh` = a background re-fetch when the tab regains focus. It skips
    // the blocking overlay, and a failure keeps the views already loaded.
    let cancelled = false;
    const fetchUserAccess = async (isRefresh = false) => {
      lastAccessFetchRef.current = Date.now();
      try {
        const request = fleetApi.viewManagement.getViewsByUserId(currentUser.id, authToken);
        const views = isRefresh ? await request : await trackAsync(request);
        if (cancelled) return;
        const topNodes = Array.isArray(views)
          ? views
          : Array.isArray(views?.children) ? views.children
          : Array.isArray(views?.data)     ? views.data
          : Array.isArray(views?.$values)  ? views.$values
          : Array.isArray(views?.items)    ? views.items
          : [];
        setNavTree(topNodes);
        // Flatten the full tree so RBAC checks work for deeply nested views.
        const allowed = flattenViewTree(topNodes);
        setAllowedViews(allowed);

        // If the current page isn't in the allowed list, navigate to the first allowed view.
        //
        // Compare canonical view IDs rather than raw paths. The old check tested
        // `VIEW_PATH_MAP[view.url] === currentPath` — exact string equality — so
        // any page with sub-tabs was unreachable on a fresh load: a view mapped
        // to /fuel/log never equals /fuel/consumption or /fuel/estimate, and the
        // user got silently bounced to the dashboard. Resolving both sides
        // through PATH_TO_ID means every tab of a permitted page is permitted,
        // which is what the permission actually grants.
        const currentPath   = pathnameRef.current;
        const currentViewId = PATH_TO_ID[currentPath];
        const isPathAllowed = allowed.some((v) => {
          const mapped = VIEW_PATH_MAP[normalizeViewKey(v)];
          if (!mapped) return false;
          if (mapped === currentPath) return true;
          return Boolean(currentViewId) && PATH_TO_ID[mapped] === currentViewId;
        });
        if (allowed.length > 0 && !isPathAllowed) {
          // Find the first navigable leaf node (no children)
          const leaves = allowed.filter(v => !v.children || v.children.length === 0);
          const hasDashboard = leaves.some(v => normalizeViewKey(v) === 'dashboard');
          const firstLeaf = leaves[0];
          const firstPath = hasDashboard
            ? '/dashboard'
            : VIEW_PATH_MAP[normalizeViewKey(firstLeaf)] || '/dashboard';
          navigateRef.current(firstPath, { replace: true });
        }
      } catch (err) {
        if (cancelled || isRefresh) return;
        console.error('Failed to fetch user access:', err);
        setAllowedViews([]);
        addToast({ id: Date.now(), type: 'error', title: 'Permission error', message: 'Could not load your permissions. Please refresh.' });
      }
    };

    fetchUserAccess();

    // Refresh on return to the tab, so Add/Edit/Delete flags an admin changed
    // while this user was away take effect without a reload. Throttled — the
    // backend still enforces every write, this only keeps the buttons honest.
    const refreshOnReturn = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastAccessFetchRef.current < ACCESS_REFRESH_MIN_MS) return;
      fetchUserAccess(true);
    };
    window.addEventListener('focus', refreshOnReturn);
    document.addEventListener('visibilitychange', refreshOnReturn);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', refreshOnReturn);
      document.removeEventListener('visibilitychange', refreshOnReturn);
    };
  }, [currentUser?.id, authToken, navigateRef, pathnameRef, trackAsync, addToast]);

  // ── Build nav items from the tree returned by getViewsByUserId ───────────────
  // Rule: nodes with children.length > 0 → sidebar group headers.
  //       nodes with no children → navigable pages (shown as items).
  const mapTreeNodeToItem = (node) => {
    const key = normalizeViewKey(node);
    // Frontend label override wins over the backend view title so renames
    // (e.g. "Theme" → "User Settings") apply everywhere: sidebar, sub-nav, topbar.
    const label = (k, fallback) => (VIEW_LABEL_KEY[k] ? t(VIEW_LABEL_KEY[k]) : fallback);
    return {
      id:            key || String(node.id),
      label:         label(key, node.title || node.label || node.name || `View ${node.id}`),
      icon:          VIEW_ICON_MAP[key] || LayoutDashboard,
      path:          toInternalPath(key),
      orderSequence: node.orderSequence ?? Infinity,
      subItems:      (node.children || [])
        .filter(c => !c.children || c.children.length === 0)
        .map(child => {
          const ck = normalizeViewKey(child);
          return {
            id:    ck || String(child.id),
            label: label(ck, child.title || child.label || child.name || `View ${child.id}`),
            path:  toInternalPath(ck),
          };
        }),
    };
  };

  // Build a single ordered array that preserves the navTree sequence from the API.
  // Each entry is either a group (isGroup: true, has items) or a standalone page (isGroup: false).
  // The sidebar renders the hierarchy exactly as View Management defines it —
  // any regrouping or flattening belongs in the Views table, not here.
  const navSections = (navTree || [])
    .map(node => {
      if (node.children && node.children.length > 0) {
        return { isGroup: true, label: node.title, id: (node.url || '').toLowerCase() || String(node.id), items: (node.children || []).map(mapTreeNodeToItem) };
      }
      return { isGroup: false, ...mapTreeNodeToItem(node) };
    })
    .filter(s => s.isGroup ? s.items.length > 0 : true);

  // Super-admin settings injection.
  const isSuperAdminRole = ['admin', 'superadmin'].includes((currentUser?.role || '').toLowerCase());
  const SETTINGS_IDS     = ['theme-settings', 'settings', 'app settings'];
  const allNavIds        = new Set(navSections.flatMap(s => s.isGroup ? s.items.map(i => i.id) : [s.id]));
  const hasSettings      = SETTINGS_IDS.some(id => allNavIds.has(id));
  if (isSuperAdminRole && !hasSettings) {
    navSections.push({ isGroup: false, id: 'settings', label: 'Settings', icon: Settings, path: '/settings', orderSequence: Infinity });
  }

  // ── ⌘K palette entries ───────────────────────────────────────────────────────
  // Flattened from the SAME nav model the sidebar renders, so the palette is
  // RBAC-safe by construction — it can never offer a page the sidebar wouldn't.
  // Sub-pages become first-class entries ("Dashboard › Compliance Report").
  const paletteEntries = navSections.flatMap(section => {
    const items = section.isGroup ? section.items : [section];
    const group = section.isGroup ? section.label : null;
    return items.flatMap(item => [
      { label: item.label, path: item.subItems?.length ? (item.subItems[0].path) : item.path, icon: item.icon, group },
      ...(item.subItems || []).map(sub => ({
        label: `${item.label} › ${sub.label}`,
        path: sub.path,
        icon: item.icon,
        group,
      })),
    ]);
  }).filter(e => e.path && e.path !== '/');

  // The label a page shows in the sidebar: the translated one when there is a
  // key for it, the Views table title otherwise.
  const navLabelFor = (item) => (VIEW_LABEL_KEY[item.id] ? t(VIEW_LABEL_KEY[item.id]) : item.label);

  /* Add new (Oct 2026): make any everyday record from any page. Each entry
     only routes to its page with `state.quickAdd`; the page opens its own Add
     form with its own code (hooks/useQuickAdd.js) once its data is in. The
     menu follows the sidebar: same groups, same order, and only pages this
     user can open. Fuel is not here yet: Fuel.js is Nour's page and does not
     read this state (see docs/FrontEnd for the two lines it needs). */
  const navPaths = new Set(navSections.flatMap(s => (s.isGroup ? s.items : [s]))
    .flatMap(i => [i.path, ...(i.subItems || []).map(x => x.path)]));
  /* `view` + `need`: the page's view key and the flag that action needs
     (view permissions, Oct 2026). A user who cannot add on a page does not
     get its entry here, same as the page hides its own Add button. Loading
     stock is an edit on Vehicle Inventory, as on that page. */
  const QUICK_ADD = {
    '/orders':             { kind: 'order',      label: 'New order',      icon: ClipboardList, view: 'orders' },
    '/sell-orders':        { kind: 'sell-order', label: 'New sell order', icon: ShoppingCart,  view: 'sellorders' },
    '/customers':          { kind: 'customer',   label: 'Add customer',   icon: Store,         view: 'customers' },
    '/drivers':            { kind: 'driver',     label: 'Add driver',     icon: Contact2,      view: 'drivers' },
    '/vehicles':           { kind: 'vehicle',    label: 'Add vehicle',    icon: Truck,         view: 'vehicles' },
    '/vehicle-inventory':  { kind: 'load',       label: 'Load stock',     icon: Boxes,         view: 'vehicle-inventory', need: 'canEdit' },
    '/maintenance-repair': { kind: 'repair',     label: 'Add repair',     icon: Wrench,        view: 'maintenance-repair' },
    '/vendors':            { kind: 'vendor',     label: 'Add vendor',     icon: Building2,     view: 'vendors' },
    '/places':             { kind: 'place',      label: 'Add place',      icon: MapPin,        view: 'places' },
    '/contacts':           { kind: 'contact',    label: 'Add contact',    icon: Users,         view: 'contacts' },
    '/items':              { kind: 'item',       label: 'Add item',       icon: Package,       view: 'items' },
  };
  const viewPerms = buildViewPermissionMap(allowedViews);
  const quickAllowed = (q) => lookupViewPermissions(viewPerms, q.view)[q.need || 'canAdd'] !== false;
  const quickActions = navSections.flatMap((section) => {
    const group = section.isGroup ? section.label : null;
    return (section.isGroup ? section.items : [section])
      .flatMap(i => [i.path, ...(i.subItems || []).map(x => x.path)])
      .filter(p => QUICK_ADD[p] && quickAllowed(QUICK_ADD[p]))
      .map(p => ({ ...QUICK_ADD[p], path: p, group }));
  });

  // ── RBAC-gated route content ──────────────────────────────────────────────────
  const renderRoutes = () => {
    // Waiting for RBAC fetch to complete — global overlay covers the UI via trackAsync.
    if (allowedViews === null && currentUser) return null;

    // User has no views assigned.
    if (allowedViews !== null && allowedViews.length === 0) {
      return (
        <div className="section-card" style={{ padding: 40, textAlign: 'center' }}>
          <Shield size={48} style={{ color: 'var(--text-muted)', marginBottom: 16 }} />
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No Access Configured</div>
          <div style={{ color: 'var(--text-muted)' }}>No access configured. Contact admin.</div>
        </div>
      );
    }

    const props = { searchQuery, currentUser, authToken, api: fleetApi, onToast: addToast, currencySettings };
    const pr = (viewId, element) => (
      <ProtectedRoute viewId={viewId} allowedViews={allowedViews} element={element} />
    );
    const getDefaultPath = (views) => {
      if (!views?.length) return '/dashboard';
      // Only navigate to leaf nodes (pages), not group headers (which have children).
      const leaves = views.filter(v => !v.children || v.children.length === 0);
      const hasDashboard = leaves.some(v => normalizeViewKey(v) === 'dashboard');
      if (hasDashboard) return '/dashboard';
      const first = leaves[0];
      return first ? (VIEW_PATH_MAP[normalizeViewKey(first)] || '/dashboard') : '/dashboard';
    };
    const firstAllowedPath = getDefaultPath(allowedViews);

    return (
      <ViewPermissionsProvider allowedViews={allowedViews}>
      <Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-muted)', fontSize: 14 }}>Loading…</div>}>
      <Routes>
        <Route path="/dashboard" element={<Navigate to="/dashboard/general" replace />} />
        <Route path="/dashboard/general"       element={pr('dashboard', <Dashboard tab="general"       {...props} />)} />
        <Route path="/dashboard/compliance"    element={pr('dashboard', <Dashboard tab="compliance"    {...props} />)} />
        <Route path="/dashboard/transactions"  element={pr('dashboard', <Dashboard tab="transactions"  {...props} />)} />
        <Route path="/vehicles"     element={pr('vehicles',     <Vehicles     {...props} />)} />
        <Route path="/drivers"      element={pr('drivers',      <Drivers      {...props} />)} />
        <Route path="/orders"       element={pr('orders',       <Orders       {...props} />)} />
        <Route path="/tracking"     element={pr('tracking',     <TrackingMap
          {...props}
          showVehiclesPanel={trackingVehiclesOpen}
          requestedChip={trackingActiveChip}
          onActiveChipChange={setTrackingActiveChip}
          onChipStatsChange={setTrackingChipStats}
          onToggleVehiclesPanel={() => setTrackingVehiclesOpen(o => !o)}
          chipRects={trackingChipRects}
          mapResetVersion={trackingMapResetVersion}
          mapSearchQuery={trackingMapSearch}
          mapSearchVersion={trackingMapSearchVersion}
          mapFlyTo={trackingMapFlyTo}
          onToggleSidebar={() => setSidebarOpen(o => !o)}
          onOpenNotif={openPanel}
        />)} />
        <Route path="/fleets"       element={pr('fleets',       <Fleets       {...props} />)} />
        <Route path="/places"       element={pr('places',       <Places       {...props} />)} />
        <Route path="/contacts"     element={pr('contacts',     <Contacts     {...props} />)} />
        <Route path="/customers"    element={pr('customers',    <Customers    {...props} />)} />
        <Route path="/vendors"      element={pr('vendors',      <Vendors      {...props} />)} />
        <Route path="/transactions" element={pr('transactions', <Transactions {...props} />)} />
        <Route path="/items"        element={pr('items',        <Items        {...props} />)} />
        <Route path="/sell-orders"       element={pr('sell-orders',      <SellOrders      {...props} />)} />
        <Route path="/vehicles-tracker" element={pr('vehicles-tracker', <VehiclesTracker {...props} />)} />
        <Route path="/admin-users" element={<Navigate to="/security/users" replace />} />
        <Route path="/organization" element={pr('organization', <Organization currentUser={currentUser} authToken={authToken} api={fleetApi} searchQuery={searchQuery} />)} />
        <Route path="/security"       element={<Navigate to="/security/users" replace />} />
        <Route path="/security/users" element={pr('security', <Security tab="users" currentUser={currentUser} authToken={authToken} api={fleetApi} onToast={addToast} />)} />
        <Route path="/security/roles" element={pr('security', <Security tab="roles" currentUser={currentUser} authToken={authToken} api={fleetApi} onToast={addToast} />)} />
        <Route path="/security/views" element={pr('security', <Security tab="views" currentUser={currentUser} authToken={authToken} api={fleetApi} onToast={addToast} />)} />
        <Route path="/zones"        element={pr('zones',        <Zones        currentUser={currentUser} authToken={authToken} api={fleetApi} onToast={addToast} searchQuery={searchQuery} currencySettings={currencySettings} />)} />
        <Route path="/geofences"    element={pr('geofences',    <Geofences    {...props} />)} />
        <Route path="/settings/required-documents" element={pr('required-documents', <RequiredDocumentsSetup currentUser={currentUser} authToken={authToken} api={fleetApi} onToast={addToast} searchQuery={searchQuery} />)} />
        <Route path="/settings/document-types" element={pr('document-types', <DocumentTypes authToken={authToken} api={fleetApi} onToast={addToast} searchQuery={searchQuery} />)} />
        <Route path="/settings/default-settings" element={pr('default-settings', <DefaultSettings authToken={authToken} api={fleetApi} onToast={addToast} searchQuery={searchQuery} />)} />
        <Route path="/settings/plans" element={pr('plan-admin', <PlanAdmin tab="plans" currentUser={currentUser} authToken={authToken} api={fleetApi} onToast={addToast} searchQuery={searchQuery} />)} />
        <Route path="/settings/plans/subscriptions" element={pr('plan-admin', <PlanAdmin tab="subscriptions" currentUser={currentUser} authToken={authToken} api={fleetApi} onToast={addToast} searchQuery={searchQuery} />)} />
        <Route path="/settings/plans/signup-codes" element={pr('plan-admin', <PlanAdmin tab="signup-codes" currentUser={currentUser} authToken={authToken} api={fleetApi} onToast={addToast} searchQuery={searchQuery} />)} />
        <Route path="/sysadmin" element={<Navigate to="/sysadmin/organizations" replace />} />
        <Route path="/sysadmin/organizations" element={pr('sysadmin', <SysAdmin tab="organizations" currentUser={currentUser} authToken={authToken} api={fleetApi} onToast={addToast} searchQuery={searchQuery} />)} />
        <Route path="/sysadmin/users" element={pr('sysadmin', <SysAdmin tab="users" currentUser={currentUser} authToken={authToken} api={fleetApi} onToast={addToast} searchQuery={searchQuery} />)} />
        <Route path="/maintenance-repair" element={pr('maintenance-repair', <MaintenanceRepair currentUser={currentUser} authToken={authToken} api={fleetApi} onToast={addToast} searchQuery={searchQuery} />)} />
        <Route path="/vehicle-inventory" element={pr('vehicle-inventory', <VehicleInventory currentUser={currentUser} authToken={authToken} api={fleetApi} searchQuery={searchQuery} onToast={addToast} />)} />
        <Route path="/fuel-management" element={<Navigate to="/fuel/log" replace />} />
        <Route path="/fuel-reports" element={<Navigate to="/fuel/consumption" replace />} />
        <Route path="/fuel" element={<Navigate to="/fuel/log" replace />} />
        <Route path="/fuel/log"         element={pr('fuel', <FuelPage tab="log"         {...props} />)} />
        <Route path="/fuel/consumption" element={pr('fuel', <FuelPage tab="consumption" {...props} />)} />
        <Route path="/fuel/estimate"    element={pr('fuel', <FuelPage tab="estimate"    {...props} />)} />
        <Route path="/settings" element={<Navigate to="/settings/theme" replace />} />
        {['theme', 'notifications', 'financial', 'orderconfig'].map(tab => (
          <Route key={tab} path={`/settings/${tab}`} element={
            (isSuperAdminRole || hasSettings)
              ? <SettingsPage tab={tab} {...props}
                  currentTheme={theme}            onThemeChange={setTheme}
                  notifSettings={notifSettings}   onNotifSettingsChange={saveAndSetNotifSettings}
                  currencySettings={currencySettings} onCurrencySettingsChange={saveAndSetCurrencySettings}
                />
              : <div className="section-card" style={{ padding: 40, textAlign: 'center' }}>
                  <Shield size={48} style={{ color: 'var(--text-muted)', marginBottom: 16 }} />
                  <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Access Denied</div>
                  <div style={{ color: 'var(--text-muted)' }}>Only the superadmin can change application settings.</div>
                </div>
          } />
        ))}
        {/* Root and unknown paths redirect to the user's first allowed view */}
        <Route path="/"  element={<Navigate to={firstAllowedPath} replace />} />
        <Route path="*"  element={<Navigate to={firstAllowedPath} replace />} />
      </Routes>
      </Suspense>
      </ViewPermissionsProvider>
    );
  };

  // All sidebar items in order for topbar label lookup.
  /* Sub-pages are included so a settings page titles itself by its own name.
     Before, only top-level items were searched, so Document Types and Required
     Documents Setup rendered with no page title at all. */
  const allSidebarItems = navSections
    .flatMap(s => s.isGroup ? s.items : [s])
    .flatMap(item => [item, ...(item.subItems || []).map(sub => ({ ...sub, parentLabel: item.label }))]);
  const activeItem = allSidebarItems.find(item => item.path === location.pathname)
    || allSidebarItems.find(item => item.id === activePage);

  // ── First-login gate ──────────────────────────────────────────────────────────
  if (firstLoginSession) {
    return (
      <FirstLoginPage
        session={firstLoginSession}
        api={fleetApi}
        onComplete={(user, token) => completeFirstLogin(token, user)}
      />
    );
  }

  // ── Auth gate ─────────────────────────────────────────────────────────────────
  if (!currentUser) {
    return (
      <Routes>
        <Route path="/login" element={
          <AuthPage
            onLogin={({ token, user, organizationId, organizationName }) => {
              login(token, {
                ...user,
                organizationId: organizationId || user.organizationId,
                companyName:    organizationName || user.companyName,
              });
            }}
            onFirstLogin={(session) => setFirstLoginSession(session)}
          />
        } />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // ── Formats the user's role into a readable label with "User" suffix ─────────
  const formatRoleLabel = (role) => {
    if (!role) return 'User';
    const map = {
      superadmin:       'Super Admin',
      admin:            'Admin',
      orgadmin:         'Org Admin',
      'org admin':      'Org Admin',
      zonemanager:      'Zone Manager',
      'zone manager':   'Zone Manager',
      fleetmanager:     'Fleet Manager',
      'fleet manager':  'Fleet Manager',
      driver:           'Driver',
      operator:         'Operator',
    };
    const key = role.toLowerCase().trim();
    // Title-case any unmapped role rather than only capitalising the first
    // letter, so "fleet manager" reads "Fleet Manager", not "Fleet manager".
    const fallback = key.replace(/\b\w/g, (c) => c.toUpperCase());
    // No " User" suffix: "Super Admin User" is clumsy, and the row is already
    // labelled by the person's name directly above it.
    return map[key] || fallback;
  };

  // ── Plan + storage (sidebar footer) ─────────────────────────────────────────
  // Plain expressions, not hooks, so they live below the auth gates and never
  // compute on logged-out renders.
  const planLabel = myPlan?.hasActiveSubscription
    ? (myPlan.planName || myPlan.planCode || '')
    : '';

  // storageUsedBytes is bytes; maxStorageMb is megabytes. Same conversion and
  // thresholds as PlanStorageSection in Settings.js — one storage language, and
  // the two meters can never disagree.
  const storageLimitBytes = myPlan?.maxStorageMb == null ? null : mbToBytes(myPlan.maxStorageMb);
  const storageUsedBytes  = myPlan?.storageUsedBytes ?? 0;
  const storageRawPct     = storageLimitBytes ? (storageUsedBytes / storageLimitBytes) * 100 : 0;
  const storageBarPct     = Math.min(100, storageRawPct);   // label may exceed 100; the bar must not
  const storageIsFull     = storageLimitBytes != null && storageUsedBytes >= storageLimitBytes;
  const storageIsNearFull = storageLimitBytes != null && !storageIsFull && storageRawPct >= 80;
  const storageFillColor  = storageIsFull     ? 'var(--danger)'
                          : storageIsNearFull ? 'var(--warning)'
                          : 'var(--primary)';

  // t() takes a key only, so placeholders are filled here rather than splitting
  // the sentence — a split sentence cannot be ordered correctly in Arabic.
  const fillKey = (key, values) =>
    Object.entries(values).reduce((s, [k, v]) => s.replace(`{${k}}`, v), t(key));

  const storageText = storageLimitBytes == null
    ? formatBytes(storageUsedBytes)
    : fillKey('sidebarStorageOf', {
        used:  formatBytes(storageUsedBytes),
        limit: formatBytes(storageLimitBytes),
      });
  const storageTitle = storageLimitBytes == null
    ? fillKey('storageNoLimit', { used: formatBytes(storageUsedBytes) })
    : fillKey('storageUsedOf', {
        used:  formatBytes(storageUsedBytes),
        limit: formatBytes(storageLimitBytes),
      });

  // Written on dismiss rather than on show: if they refresh before closing it,
  // the message has not actually been read yet, so it should come back.
  const dismissPlanExpired = () => {
    setShowPlanExpired(false);
    try {
      if (currentUser?.id) sessionStorage.setItem(`fleet_plan_expired_ack_${currentUser.id}`, '1');
    } catch (_) { /* storage unavailable — dismissal just won't persist */ }
  };

  // ── Shared utility buttons (Bell / Theme / Language) — rendered in both topbar modes ──
  const TopbarUtilityButtons = () => (
    <>
      <div style={{ position: 'relative' }}>
        <button className="icon-btn notif-btn" onClick={openPanel}>
          <Bell size={18} />
          {unreadCount > 0 && (
            <span className="notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </button>
        {showNotifPanel && (
          <NotificationPanel
            notifications={notifications} unreadCount={unreadCount}
            hasMore={hasMore} loading={listLoading} onLoadMore={() => loadNotifications(false)}
            onMarkRead={markRead} onMarkAllRead={markAllRead}
            onDeleteNotif={deleteNotification} onClose={() => setShowNotifPanel(false)}
            onNavigate={navigate}
          />
        )}
      </div>
      {/* The theme switch moved into your menu at the foot of the sidebar
          (Oct 2026), with plan, storage and sign out. */}
    </>
  );

  // ── Main shell ────────────────────────────────────────────────────────────────
  return (
    <div className={`app-shell${mobileNavOpen ? ' nav-open' : ''}`}>
      {/* ── Mobile sidebar backdrop overlay ──────────────────────────────── */}
      <div className="sidebar-overlay" onClick={() => setMobileNavOpen(false)} />

      {/* ── Sidebar Navigation (Oct 2026: components/Sidebar.js) ──────────── */}
      <Sidebar
        navSections={navSections}
        pathname={location.pathname}
        navigate={navigate}
        open={sidebarOpen}
        mobileOpen={mobileNavOpen}
        onToggle={toggleSidebar}
        onCloseMobile={() => setMobileNavOpen(false)}
        collapsedSubnavs={collapsedSubnavs}
        onToggleSubnav={toggleSubnav}
        onOpenSubnav={(id) => setCollapsedSubnavs(prev => { const n = new Set(prev); n.delete(id); return n; })}
        labelFor={navLabelFor}
        signals={navSignals}
        quickActions={quickActions}
        account={{
          name: currentUser.fullName,
          initials: currentUser.fullName.split(' ').filter(Boolean).map(part => part[0]).slice(0, 2).join('').toUpperCase(),
          role: formatRoleLabel(currentUser.role),
          planLabel,
          planExpired: Boolean(myPlan?.isExpired),
          storage: {
            show: Boolean(myPlan?.hasActiveSubscription),
            text: storageText,
            title: storageTitle,
            hasLimit: storageLimitBytes != null,
            pctLabel: `${Math.round(storageRawPct)}%`,
            barPct: storageBarPct,
            color: storageFillColor,
            isFull: storageIsFull,
            isNearFull: storageIsNearFull,
          },
        }}
        theme={theme}
        onToggleTheme={() => setTheme(prev => (prev === 'dark' ? 'light' : 'dark'))}
        onLogout={logout}
        settingsPath={navPaths.has('/settings/theme') ? '/settings/theme' : null}
        guardLeave={() => unsavedGridChanges() === 0 || confirmLeaveGrid()}
        t={t}
      />

      {/* ── Main Content Area ─────────────────────────────────────────────── */}
      <div className={`main-area${isTrackingPage ? ' tracking-mode' : ''}`}>

        {/* ── Topbar (normal or tracking-overlay) ─────────────────────────── */}
        <header className={`topbar${isTrackingPage ? ' topbar-tracking' : ''}`}>
          {isTrackingPage ? (
            /* ── Tracking overlay topbar ─────────────────────────────────── */
            <>
              <button className="mobile-menu-btn" onClick={() => setMobileNavOpen(true)} aria-label="Open navigation">
                <Menu size={18} />
              </button>
              {/* Google-style persistent search box with autocomplete */}
              <div style={{ position: 'relative', flex: 1, maxWidth: 520, minWidth: 0 }}>
                <form
                  className="tracking-topbar-search"
                  onSubmit={e => { e.preventDefault(); if (trackingMapSearch.trim()) { setTrackingMapSearchVersion(v => v + 1); setTrackingMapSuggestionsOpen(false); } }}
                >
                  <Search size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  <input
                    placeholder="Search for a location…"
                    value={trackingMapSearch}
                    onChange={e => setTrackingMapSearch(e.target.value)}
                    onBlur={() => setTimeout(() => setTrackingMapSuggestionsOpen(false), 150)}
                    onFocus={() => trackingMapSuggestions.length > 0 && setTrackingMapSuggestionsOpen(true)}
                    onKeyDown={e => { if (e.key === 'Escape') setTrackingMapSuggestionsOpen(false); }}
                  />
                </form>
                {trackingMapSuggestionsOpen && trackingMapSuggestions.length > 0 && (
                  <ul className="tracking-search-suggestions">
                    {trackingMapSuggestions.map((s, i) => (
                      <li key={i} onMouseDown={() => {
                        setTrackingMapSearch(s.display_name);
                        setTrackingMapSuggestionsOpen(false);
                        setTrackingMapFlyTo({ lat: parseFloat(s.lat), lon: parseFloat(s.lon), v: Date.now() });
                      }}>
                        <MapPin size={12} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
                        <span>{s.display_name}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Refresh map button */}
              <button
                className="tracking-nav-btn"
                onClick={() => {
                  setTrackingMapResetVersion(v => v + 1);
                  setTrackingVehiclesOpen(true);
                  setTrackingActiveChip(null);
                  setTrackingMapSearch('');
                  setTrackingMapSuggestions([]);
                  setTrackingMapSuggestionsOpen(false);
                }}
                title="Reset map to initial view"
                style={{ color: 'var(--text-primary)', borderColor: 'var(--border)', gap: 5 }}
              >
                <RotateCcw size={13} />
                <span className="tracking-btn-label">Reset view</span>
              </button>

              {/* Status chips — Moving / Idle / No Signal */}
              <div className="tracking-chips-row" style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 16 }}>
                {[
                  { key: 'moving',   label: 'Moving',    color: '#16a34a', count: trackingChipStats.moving   },
                  { key: 'idle',     label: 'Idle',      color: '#f59e0b', count: trackingChipStats.idle     },
                  { key: 'nosignal', label: 'No signal', color: '#ef4444', count: trackingChipStats.noSignal },
                ].map(({ key, label, color, count }) => (
                  <button
                    key={key}
                    className={`tracking-nav-btn${trackingActiveChip === key ? ' active' : ''}`}
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTrackingChipRects(prev => ({ ...prev, [key]: { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right, width: rect.width } }));
                      setTrackingActiveChip(prev => prev === key ? null : key);
                    }}
                    title={label}
                    /* Oct 2026 UI pass: grey border + coloured dot at rest, a
                       soft tint of its colour when pressed (was a full fill). */
                    style={trackingActiveChip === key
                      ? { background: `${color}1f`, borderColor: color, color: 'var(--text-primary)' }
                      : { color: 'var(--text-primary)' }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ fontWeight: 600 }}>{count}</span>
                    <span>{label}</span>
                  </button>
                ))}
              </div>

              {/* Right: standard utility icons */}
              <div className="topbar-right">
                <TopbarUtilityButtons />
              </div>
            </>
          ) : (
            /* ── Normal topbar ───────────────────────────────────────────── */
            <>
              <div className="topbar-left">
                <button className="mobile-menu-btn" onClick={() => setMobileNavOpen(true)} aria-label="Open navigation">
                  <Menu size={18} />
                </button>
                {/* Just the page name. Today's date was printed here on every
                    screen — the user owns a clock, and a date that isn't tied
                    to the data on screen is furniture. The organisation name
                    went too: you only ever see your own, so it told you
                    nothing you didn't already know. */}
                <div className="page-heading">
                  {/* A sub-page names its section first, the way the command
                      palette does: "Dashboard › General Report". A report
                      then says its own name once more in its toolbar with a
                      description, which is fine — the h1 is the place, the
                      toolbar is the subject. */}
                  <h1 className="page-title">
                    {activeItem?.parentLabel && <span className="page-title-parent">{activeItem.parentLabel} <span aria-hidden="true">›</span> </span>}
                    {activeItem?.label}
                  </h1>
                </div>
              </div>
              <div className="topbar-right">
                <div className="search-box">
                  <Search size={15} />
                  <input
                    type="text"
                    placeholder={t('searchPlaceholder')}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                  {/* The ⌘K badge is gone. A shortcut hint parked permanently in
                      the chrome is furniture: it's read once, then sits there for
                      every session after. The shortcut still works — it's bound
                      globally — and the search field is its own entry point. */}
                </div>
                <button className="icon-btn" onClick={() => setShowAnnouncement(true)} title="Send Announcement">
                  <Megaphone size={18} />
                </button>
                <TopbarUtilityButtons />
              </div>
            </>
          )}
        </header>

        <main className={`page-content${isTrackingPage ? ' page-content-map' : ''}`}>
          {renderRoutes()}
        </main>
      </div>

      <NotificationToast toasts={toastQueue} onDismiss={dismissToast} />
      <CommandPalette
        open={showPalette}
        onClose={() => setShowPalette(false)}
        entries={paletteEntries}
        onNavigate={(path) => navigate(path)}
      />
      {showAnnouncement && (
        <AnnouncementModal
          onClose={() => setShowAnnouncement(false)}
          authToken={authToken}
          currentUser={currentUser}
          onToast={addToast}
        />
      )}
      {showPlanExpired && (
        <PlanExpiredModal expiryDateUtc={myPlan?.expiryDateUtc} onClose={dismissPlanExpired} />
      )}
      <LoadingOverlay />
    </div>
  );
}
